import { useCallback, useEffect, useRef, useState } from "react";
import { AlertType, BannedPatient, Clinic } from "../../models";
import { ConfirmDeletePatient } from "./ConfirmDeletePatient";
import { useAlertContext, useUserContext } from "../../contexts";
import { useMsal } from "@azure/msal-react";
import { apiAccessRequest } from "../../constants";

interface Props {
	bannedPatients: BannedPatient[];
	searchText: string;
	clinic?: Clinic;
	refreshPatients: () => void;
}

export const BannedPatientsTable = ({
	bannedPatients,
	searchText,
	clinic,
	refreshPatients
}: Props) => {
	const { account } = useUserContext();
	const { instance } = useMsal();
	const { showAlert } = useAlertContext();
	const [filteredPatients, setFilteredpatients] = useState<BannedPatient[]>([]);
	const [selectedPatient, setSelectedPatient] = useState<BannedPatient | null>(
		null
	);
	const [openConfirmDelete, setOpenConfirmDelete] = useState(false);
	const [editingPatientId, setEditingPatientId] = useState<number | null>(null);
	const notesEl = useRef<HTMLTextAreaElement | null>(null);

	useEffect(() => {
		setFilteredpatients(
			bannedPatients.filter((PatientSearch) =>
				PatientSearch.patientName
					.toLowerCase()
					.includes((searchText || "").toLowerCase())
			)
		);
	}, [bannedPatients, searchText]);

	const handleDeleteClick = useCallback((bannedPatient: BannedPatient) => {
		setSelectedPatient(bannedPatient);
		setOpenConfirmDelete(true);
		setEditingPatientId(null);
	}, []);

	const handleCloseConfirmDelete = useCallback(
		(updated: boolean) => {
			if (updated) {
				refreshPatients();
			}
			setSelectedPatient(null);
			setOpenConfirmDelete(false);
		},
		[refreshPatients]
	);

	const handleNotesClick = useCallback((bannedPatient: BannedPatient) => {
		setSelectedPatient(bannedPatient);
		setEditingPatientId(bannedPatient.id);
	}, []);

	const handleNotesCancelClick = useCallback(() => {
		setSelectedPatient(null);
		setEditingPatientId(null);
	}, []);

	const updateBannedpatient = useCallback(
		async (bannedPatient: BannedPatient, notes: string) => {
			try {
				const requestUrl = `${process.env.REACT_APP_CONSULTS_URL}patient/bannedpatient`;
				const tokenResponse = await instance.acquireTokenSilent({
					account,
					scopes: apiAccessRequest.scopes
				});
				const headers = {
					Authorization: `Bearer ${tokenResponse.accessToken}`,
					"Content-Type": "application/json"
				};
				const body = { id: bannedPatient.id, notes };
				const response = await fetch(requestUrl, {
					method: "PATCH",
					headers,
					body: JSON.stringify(body)
				});

				if (!response.ok) {
					return;
				}
				showAlert({
					message: `Notes for ${bannedPatient.patientName} have been updated`,
					alertType: AlertType.success
				});
				refreshPatients();
			} catch (error) {
				showAlert({
					message: `There was an error updating notes for ${bannedPatient.patientName}`,
					alertType: AlertType.error
				});
			}
		},
		[account, instance, refreshPatients, showAlert]
	);

	const handleNotesSaveClick = useCallback(
		async (bannedPatient: BannedPatient) => {
			const notes = notesEl.current?.value;
			await updateBannedpatient(bannedPatient, notes ?? "");
			setSelectedPatient(null);
			setEditingPatientId(null);
		},
		[updateBannedpatient]
	);

	return (
		<>
			{filteredPatients.length > 0 && (
				<>
					<table className="ch-table w-full">
						<thead>
							<tr>
								<th>Patient Id</th>
								<th>Name</th>
								<th>Date of Birth</th>
								<th>Notes</th>
								<th>Delete</th>
							</tr>
						</thead>
						<tbody>
							{filteredPatients?.map((bannedPatient) => (
								<tr key={bannedPatient.id}>
									<td>{bannedPatient.bpPatientId}</td>
									<td>{bannedPatient.patientName}</td>
									<td>{bannedPatient.dateOfBirth}</td>
									<td className="flex">
										{editingPatientId !== bannedPatient.id ? (
											<>
												{bannedPatient.notes}
												<button
													className="ch-button ch-button-clear ml-4"
													style={{ padding: 0 }}
													onClick={() => handleNotesClick(bannedPatient)}
												>
													<img
														src="images/svg/pencil-blue-outline.svg"
														width="20"
														height="20"
														alt="edit notes"
													/>
												</button>
											</>
										) : (
											<>
												<textarea
													className="ch-border w-full"
													style={{ resize: "none", padding: "4px 12px" }}
													rows={4}
													ref={notesEl}
													defaultValue={bannedPatient.notes}
												/>
												<button
													className="ch-button ch-button-clear"
													style={{ padding: "0 12px" }}
													onClick={() => handleNotesCancelClick()}
												>
													<img
														src="images/svg/close.svg"
														alt="cancel"
														width="20"
														height="20"
													/>
												</button>
												<button
													className="ch-button ch-button-clear"
													style={{ padding: 0 }}
													onClick={() => handleNotesSaveClick(bannedPatient)}
												>
													<img
														src="images/svg/save-blue.svg"
														alt="save"
														width="32"
														height="32"
													/>
												</button>
											</>
										)}
									</td>
									<td>
										<button
											className="ch-button ch-button-clear"
											style={{ padding: 0 }}
											onClick={() => handleDeleteClick(bannedPatient)}
										>
											<img
												src="images/svg/delete-blue-outline.svg"
												alt="delete"
												width="32"
												height="32"
											/>
										</button>
									</td>
								</tr>
							))}
						</tbody>
					</table>
					{selectedPatient && (
						<ConfirmDeletePatient
							patient={selectedPatient}
							clinic={clinic}
							open={openConfirmDelete}
							onClose={handleCloseConfirmDelete}
						/>
					)}
				</>
			)}
			{filteredPatients.length === 0 && (
				<div
					className="flex justify-center ch-l-text"
					style={{ marginTop: 24 }}
				>
					{`There aren't any banned patients for ${clinic?.name}  ${searchText !== "" ? "with name " + searchText : ""}`}
				</div>
			)}
		</>
	);
};
